import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './lib/store';
import ErrorBoundary from './components/ErrorBoundary';

// Internal ops console (/adm) — lazy so its charts + mock data stay out of the tenant bundle
const AdmPage = lazy(() => import('./pages/adm'));
// 360° walk-through tour (/tour360) — lazy so Pannellum + its CSS only load on that route
const Tour360Page = lazy(() => import('./pages/tour360'));

// Layouts
import DashboardLayout from './components/layout/DashboardLayout';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PortfolioPage from './pages/PortfolioPage';
import PolygonWorkspacePage from './pages/PolygonWorkspacePage';
import PolygonsPage from './pages/PolygonsPage';
import AnalysisPage from './pages/AnalysisPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ReportsPage from './pages/ReportsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LandingPage from './pages/LandingPage';

// Protected Route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const isRevalidating = useAuthStore((state) => state.isRevalidating);

    // Show loading while revalidating session with server
    if (isRevalidating) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
                    <p className="mt-3 text-sm text-gray-500">Validating session...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        // The marketing landing IS the public homepage, rendered in-app at "/"
        // (clean URL, no exposed .html file). Deep links to protected routes
        // still go to /login so the post-login "next" redirect keeps working.
        if (typeof window !== 'undefined' && window.location.pathname === '/') {
            return <LandingPage />;
        }
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

function App() {
    const revalidateSession = useAuthStore((state) => state.revalidateSession);
    const user = useAuthStore((state) => state.user);

    // On mount: revalidate session with the server if we have persisted user data
    useEffect(() => {
        if (user) {
            revalidateSession();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <ErrorBoundary>
            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        borderRadius: '12px',
                        background: '#1f2937',
                        color: '#f9fafb',
                    },
                    success: {
                        iconTheme: {
                            primary: '#10b981',
                            secondary: '#f9fafb',
                        },
                    },
                    error: {
                        iconTheme: {
                            primary: '#ef4444',
                            secondary: '#f9fafb',
                        },
                    },
                }}
            />

            <Routes>
                {/* Public routes. Registration is invite-only (admins create users),
                    so there is no /register route — stale links fall through to "*". */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />

                {/* Internal ops console — own placeholder gate, separate from tenant auth */}
                <Route
                    path="/adm"
                    element={
                        <Suspense fallback={<div className="min-h-screen bg-gray-50" />}>
                            <AdmPage />
                        </Suspense>
                    }
                />

                {/* 360° walk-through tour — public, full screen, outside the dashboard layout */}
                <Route
                    path="/tour360"
                    element={
                        <Suspense fallback={<div className="fixed inset-0 bg-[#0F0F14]" />}>
                            <Tour360Page />
                        </Suspense>
                    }
                />

                {/* Protected routes */}
                <Route
                    path="/"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<DashboardPage />} />
                    <Route path="polygons" element={<PortfolioPage />} />
                    <Route path="polygons/:id" element={<PolygonWorkspacePage />} />
                    <Route path="polygons-legacy" element={<PolygonsPage />} />
                    <Route path="analysis" element={<AnalysisPage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </ErrorBoundary>
    );
}

export default App;
